import type { Action } from '@rxsv/core';
import type { AwsRumConfig } from 'aws-rum-web';
import { AwsRum } from 'aws-rum-web';

import { isAppError } from '../errors';

export interface ErrorMonitoringService {
    initialize(): void;
    captureError(error: unknown): void;
    captureErrorAction<T extends Action>(action: T): void;
}

export class CloudWatchRUMErrorMonitoringService implements ErrorMonitoringService {
    private readonly client: AwsRum;

    constructor(
        private readonly applicationId: string,
        private readonly applicationRegion: string,
        private readonly applicationVersion: string,
        private readonly applicationEnvironment: string,
        private readonly guestRoleArn: string,
        private readonly identityPoolId: string,
        awsRumOpts: Partial<AwsRumConfig> = {},
    ) {
        this.client = new AwsRum(this.applicationId, '1.0.0', this.applicationRegion, {
            ...awsRumOpts,
            allowCookies: true,
            sessionSampleRate: 1,
            endpoint: 'https://dataplane.rum.eu-central-1.amazonaws.com',
            enableXRay: false,
            // using no default telemetries (plugins) other than `errors`
            // because they would measure mostly the parent application
            // `errors` needs to be enabled to use `.captureError` and `.captureErrorActions`
            // we set `ignore: () => true` to ignore all 'error' and 'unhandledRejection' that would be caputured on window
            // because they are probably the concern of the parent application
            //
            // see: https://github.com/aws-observability/aws-rum-web/blob/main/src/plugins/event-plugins/JsErrorPlugin.ts
            telemetries: [['errors', { ignore: () => true }]],
            guestRoleArn,
            identityPoolId,
            enableRumClient: false,
            sessionAttributes: {
                app: 'portletce',
                appStage: this.applicationEnvironment,
                appVersion: this.applicationVersion,
            },
        });
    }

    public initialize(): void {
        if (
            this.applicationId &&
            this.applicationRegion &&
            this.guestRoleArn &&
            this.identityPoolId
        )
            this.client.enable();
    }

    public captureError(error: unknown) {
        this.client.recordError(error);
    }

    public captureErrorAction<T extends Action>(action: T) {
        const exception =
            'payload' in action && (isAppError(action.payload) || action.payload instanceof Error)
                ? Object.assign(
                      Object.create(Object.getPrototypeOf(action.payload)),
                      action.payload,
                      {
                          name: `${action.type}: ${action.payload.name}`,
                          message: `[${action.type}: ${action.payload.name}] ${action.payload.message}`,
                          stack: action.payload.stack,
                      },
                  )
                : action;

        this.client.recordError(exception);
    }
}

export class MockErrorMonitoringService implements ErrorMonitoringService {
    public initialize(): void {}
    public captureError(_error: unknown): void {}
    public captureErrorAction<T extends Readonly<{ type: string }>>(_action: T): void {}
}
